import React, { useCallback, useMemo } from 'react';
import {
  BodyText,
  checkIfRecaptchaEnabled,
  Form,
  TEST_GRECAPTCHA,
  TextLink,
  useNoticeBannersStackContext,
  useReCaptcha,
} from '@sweetgreen/garnish';

import { PageWrapper } from '@order/components';
import { useCustomer } from '@order/Customer';
import type { TranslationKeys } from '@order/Localization';
import { useLocalizationContext } from '@order/Localization';
import { formatBirthdayForSubmit, getMonthNames } from '@order/utils';

import { useJoinForm } from '../../hooks';
import type { JoinFormData } from '../../utils';
import { TermsAndConditions } from '../TermsAndConditions';

export const JoinForm = (props: JoinFormProps) => {
  const {
    predefinedEmail,
    isBirthdayRequired = false,
    hasPassword = false,
    hasZipCode = false,
    shouldUseRecaptcha = false,
    onPressJoin,
    onPressSignIn,
  } = props;

  const { isSigningUp } = useCustomer();
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── ReCaptcha ───────────────────────────────────────────────────

  const { reCaptchaMachine, reCaptchaRef } = useReCaptcha();

  const [reCaptchaState] = reCaptchaMachine;
  const isLoadingRecaptcha = reCaptchaState.matches('loading');
  const isExecutingRecaptcha = reCaptchaState.matches('executing');

  // ────────────────────────────────────────────────────────────────────

  const { form } = useJoinForm({
    email: predefinedEmail,
    isBirthdayRequired,
    hasPassword,
    hasZipCode,
  });
  const { control, formState, handleSubmit } = form;
  const { errors, isValid, isSubmitting } = formState;

  // ─── Helpers ────────────────────────────────────────────────────────

  const signUpUser = useCallback(
    async (signUpFormData: JoinFormData) => {
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        terms,
        birthday = '',
        grecaptcha = TEST_GRECAPTCHA,
        ...formData
      } = signUpFormData;

      const formattedBirthday = formatBirthdayForSubmit(birthday);

      await onPressJoin({
        ...formData,
        ...(formattedBirthday ? { birthday: formattedBirthday } : {}),
        terms: true,
        grecaptcha,
      });

      // reset recaptcha after request
      reCaptchaRef.current?.resetRecaptcha();
    },
    [onPressJoin, reCaptchaRef],
  );

  const onRecaptchaVerify = useCallback(
    async (grecaptcha: string) => {
      if (!grecaptcha) return;

      const formValues = form.getValues();

      await signUpUser({ ...formValues, grecaptcha });
    },
    [form, signUpUser],
  );

  const onRecaptchaError = useCallback(() => {
    addNoticeBanner({ text: t('general.error'), palette: 'caution' });
  }, [addNoticeBanner, t]);

  const submitForm = useMemo(() => {
    const isRecaptchaEnabled = checkIfRecaptchaEnabled() && shouldUseRecaptcha;

    return handleSubmit(async (formData) => {
      await (isRecaptchaEnabled
        ? reCaptchaRef.current?.executeRecaptcha()
        : signUpUser(formData));
    });
  }, [reCaptchaRef, shouldUseRecaptcha, handleSubmit, signUpUser]);

  // ────────────────────────────────────────────────────────────────────

  const monthNames = getMonthNames().map((key) => t(key as TranslationKeys));

  // ────────────────────────────────────────────────────────────────────

  const shouldShowLoadingSubmitBtn =
    isSubmitting || isSigningUp || isExecutingRecaptcha;
  const shouldDisableSubmitBtn = isLoadingRecaptcha || !isValid;

  // ─────────────────────────────────────────────────────────────────

  return (
    <PageWrapper>
      <Form.Container
        reCaptchaRef={reCaptchaRef}
        reCaptchaMachine={reCaptchaMachine}
        onRecaptchaVerify={onRecaptchaVerify}
        onRecaptchaError={onRecaptchaError}
      >
        <Form.Title>{t('join.title')}</Form.Title>

        <Form.Subtitle direction="row">
          <BodyText size={3}>{t('join.subtitle')} </BodyText>

          <TextLink
            accessibilityLabel={t('login.title')}
            onPress={onPressSignIn}
          >
            {t('login.title')}
          </TextLink>
        </Form.Subtitle>

        {/* ========== first name ========== */}

        <Form.Group>
          <Form.TextField
            name="firstName"
            control={control}
            rules={{ required: true }}
            label={t('form.first-name')}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="name-given"
            autoCapitalize="words"
            clearButtonType="icon"
            notice={errors.firstName?.message}
            disabled={isSigningUp}
          />
        </Form.Group>

        {/* ========== last name ========== */}

        <Form.Group>
          <Form.TextField
            name="lastName"
            control={control}
            rules={{ required: true }}
            label={t('form.last-name')}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="name-family"
            autoCapitalize="words"
            clearButtonType="icon"
            notice={errors.lastName?.message}
            disabled={isSigningUp}
          />
        </Form.Group>

        {/* ========== email ========== */}

        <Form.Group>
          <Form.TextField
            name="email"
            control={control}
            rules={{ required: true }}
            keyboardType="email-address"
            label={t('form.email')}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="email"
            clearButtonType="icon"
            notice={errors.email?.message}
            disabled={isSigningUp || Boolean(predefinedEmail)}
          />
        </Form.Group>

        {/* ========== phone number ========== */}

        <Form.Group>
          <Form.TextFieldPhoneNumber
            name="phoneNumber"
            control={control}
            rules={{ required: true }}
            label={t('form.phone-number')}
            clearButtonType="icon"
            maxLength={10}
            hideSubmitButton
            onSubmit={submitForm}
            notice={errors.phoneNumber?.message}
            disabled={isSigningUp}
            helperText={t('join.phone-number-helper-text')}
          />
        </Form.Group>

        {/* ========== password ========== */}

        {hasPassword ? (
          <Form.Group>
            <Form.TextFieldPassword
              name="password"
              control={control}
              rules={{ required: true }}
              label={t('form.password')}
              clearButtonType="icon"
              hideSubmitButton
              onSubmit={submitForm}
              autoComplete="password-new"
              notice={errors.password?.message}
              disabled={isSigningUp}
            />
          </Form.Group>
        ) : null}

        {/* ========== zip code ========== */}

        {hasZipCode ? (
          <Form.Group>
            <Form.TextFieldZipCode
              name="zipCode"
              control={control}
              rules={{ required: true }}
              label={t('form.zip-code')}
              clearButtonType="icon"
              maxLength={5}
              hideSubmitButton
              onSubmit={submitForm}
              notice={errors.zipCode?.message}
              disabled={isSigningUp}
              helperText={t('join.zip-code-helper-text')}
            />
          </Form.Group>
        ) : null}

        {/* ========== birthday ========== */}

        <Form.Group>
          <Form.BirthdayPicker
            name="birthday"
            control={control}
            label={t('form.birthday')}
            accessibilityLabel={t('form.birthday')}
            monthLabel={t('general.month')}
            dayLabel={t('general.day')}
            helperText={t(
              isBirthdayRequired
                ? 'join.birthday-helper-text-required'
                : 'join.birthday-helper-text',
            )}
            monthNames={monthNames}
          />
        </Form.Group>

        {/* ========== terms and conditions ========== */}

        <TermsAndConditions
          control={control}
          errorMessage={errors.terms?.message}
        />

        {/* ========== ========== ========== */}

        <Form.SubmitButton
          isLoading={shouldShowLoadingSubmitBtn}
          disabled={shouldDisableSubmitBtn}
          accessibilityLabel={t('join.create-account')}
          onPress={submitForm}
        >
          {t('join.create-account')}
        </Form.SubmitButton>
      </Form.Container>
    </PageWrapper>
  );
};

// ─── Types ──────────────────────────────────────────────────────────────────────

type JoinFormProps = Readonly<{
  predefinedEmail?: string;
  isBirthdayRequired?: boolean; // To be removed together with CELS-1883-auth-migration-enabled.
  hasPassword?: boolean; // To be removed together with CELS-1883-auth-migration-enabled.
  hasZipCode?: boolean; // To be removed together with CELS-1883-auth-migration-enabled.
  shouldUseRecaptcha?: boolean; // To be removed together with CELS-1883-auth-migration-enabled.

  onPressJoin: (payload: JoinFormData) => Promise<void>;
  onPressSignIn: () => void;
}>;
